import { useEffect, useState } from 'react';

import { ApiError, api } from '../../lib/api.js';

/**
 * Choose (or change) a username: the name everyone sees on Prism Engine
 * Cloud instead of a real name. Usernames are unique; this checks while you
 * type.
 */
export function UsernameForm({ current, onSaved, submitLabel = 'Save' }: { current: string | null; onSaved: (username: string) => void; submitLabel?: string }) {
  const [value, setValue] = useState(current ?? '');
  const [check, setCheck] = useState<{ ok: boolean; reason: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const u = value.trim();
    if (!u || u === current) {
      setCheck(null);
      return;
    }
    const timer = window.setTimeout(() => {
      api
        .get<{ ok: boolean; reason: string | null }>(`/account/username/check?u=${encodeURIComponent(u)}`)
        .then(setCheck)
        .catch(() => setCheck(null));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [value, current]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.put<{ username: string }>('/account/username', { username: value.trim() });
      onSaved(r.username);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that username.');
    } finally {
      setBusy(false);
    }
  }

  const unchanged = value.trim() === (current ?? '');
  return (
    <form className="username-form" onSubmit={save}>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="e.g. PixelFox"
        maxLength={20}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-label="Username"
      />
      <button type="submit" className="primary" disabled={busy || unchanged || !value.trim() || check?.ok === false}>
        {busy ? 'Saving…' : submitLabel}
      </button>
      {check && <p className={check.ok ? 'username-ok' : 'login-error'}>{check.ok ? 'Available ✓' : check.reason}</p>}
      {error && <p className="login-error">{error}</p>}
    </form>
  );
}

/** The first sign-in: pick a username before anything else. */
export function ChooseUsername({ onDone }: { onDone: (username: string) => void }) {
  return (
    <div className="site-page username-page">
      <h1>Pick your username</h1>
      <p className="muted">
        This is the name everyone sees on Prism Engine Cloud: on your games, in the arcade and in the app. Your real name is never shown. You can change it any time on your Account page.
      </p>
      <UsernameForm current={null} onSaved={onDone} submitLabel="Let's go" />
      <p className="muted small">3–20 letters, numbers, dots, dashes or underscores. Everyone's username is different.</p>
    </div>
  );
}
